
import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Alert,
  Button,
  Skeleton,
  Chip,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Stack,
  Badge,
  LinearProgress,
  CardHeader,
  Fade,
  Zoom,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  LocationOn,
  AccountCircle,
  CalendarToday,
  Description,
  MedicalServices,
  Refresh,
  VerifiedUser,
  Smartphone,
  Assignment,
  LocalHospital,
  PhotoCamera,
  Home,
  Map,
  Check,
  Close,
  Info,
  Warning,
  AdminPanelSettings,
  Business,
  Favorite,
  Star,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// Enhanced modern theme with better color palette
const medstownTheme = createTheme({
  palette: {
    primary: {
      main: "#2563eb",
      light: "#60a5fa",
      dark: "#1d4ed8",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#10b981",
      light: "#34d399",
      dark: "#047857",
    },
    success: {
      main: "#059669",
      light: "#10b981",
      dark: "#047857",
    },
    warning: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    error: {
      main: "#ef4444",
      light: "#f87171",
      dark: "#dc2626",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.2,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    body1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    body2: {
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.4,
      color: "#64748b",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 500,
      color: "#94a3b8",
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          border: "1px solid #f1f5f9",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
});

// Enhanced Info Card Component with better styling
const InfoCard = ({ icon, label, value, status, color = "primary" }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success.main";
      case "warning":
        return "warning.main";
      case "error":
        return "error.main";
      default:
        return "text.primary";
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        borderBottom: "1px solid #f1f5f9",
        "&:last-child": { borderBottom: "none" },
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: "#f8fafc",
          transform: "translateX(4px)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            p: 1,
            borderRadius: "12px",
            bgcolor: `${color}.main`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 40,
            height: 40,
          }}
        >
          {icon}
        </Box>
        <Box flex={1}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: getStatusColor(status),
              wordBreak: "break-word",
            }}
          >
            {value || "Not provided"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

// Enhanced Loading Skeleton
const LoadingSkeleton = () => (
  <Container maxWidth="xl" sx={{ py: 4 }}>
    {/* Header Skeleton */}
    {/* <Paper sx={{ p: 4, mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={3}>
        <Skeleton variant="circular" width={80} height={80} />
        <Box flex={1}>
          <Skeleton variant="text" width={250} height={40} />
          <Skeleton variant="text" width={180} height={24} sx={{ mt: 1 }} />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
          </Stack>
        </Box>
      </Stack>
    </Paper> */}

    {/* Cards Skeleton */}
    <Grid container spacing={3}>
      {[1, 2, 3].map((section) => (
        <Grid item xs={12} md={6} lg={4} key={section}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: "1px solid #f1f5f9" }}>
                <Skeleton variant="text" width={200} height={32} />
              </Box>
              {[...Array(6)].map((_, index) => (
                <Box key={index} sx={{ p: 3, borderBottom: "1px solid #f1f5f9" }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 2 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={150} height={24} />
                    </Box>
                  </Stack>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Container>
);

// User Status Chip
const UserStatusChip = ({ userData }) => {
  const isActive = userData?.token;
  return (
    <Chip
      icon={isActive ? <Check /> : <Close />}
      label={isActive ? "Active" : "Inactive"}
      color={isActive ? "success" : "error"}
      variant="filled"
      size="small"
    />
  );
};

// User Type Chip
const UserTypeChip = ({ userType }) => {
  const getIcon = () => {
    switch (userType?.toLowerCase()) {
      case "admin":
        return <AdminPanelSettings fontSize="small" />;
      case "doctor":
        return <MedicalServices fontSize="small" />;
      case "patient":
        return <Person fontSize="small" />;
      default:
        return <AccountCircle fontSize="small" />;
    }
  };

  const getColor = () => {
    switch (userType?.toLowerCase()) {
      case "admin":
        return "error";
      case "doctor":
        return "primary";
      case "patient":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <Chip
      icon={getIcon()}
      label={userType || "User"}
      color={getColor()}
      variant="outlined"
      size="small"
    />
  );
};

export default function UserActivity({
  id,
  loading,
  userData,
  setUserData,
  setLoading,
  error,
  setError,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const handleRetry = () => {
    window.location.reload();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <ThemeProvider theme={medstownTheme}>
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
          <LoadingSkeleton />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={medstownTheme}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in>
            <Alert
              severity="error"
              sx={{ borderRadius: 3 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRetry}
                  startIcon={<Refresh />}
                >
                  Retry
                </Button>
              }
            >
              <Typography variant="h6">Error Loading User Data</Typography>
              {error}
            </Alert>
          </Fade>
        </Container>
      </ThemeProvider>
    );
  }

  if (!userData) {
    return (
      <ThemeProvider theme={medstownTheme}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              <Typography variant="h6">No User Data Found</Typography>
              The requested user profile could not be found.
            </Alert>
          </Fade>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={medstownTheme}>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh",}}>
        <Container maxWidth="xl" >
          {/* Enhanced Three Section Cards */}
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12} md={6} lg={4}>
              <Zoom in timeout={800}>
                <Card>
                  <CardHeader
                    avatar={
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "12px",
                          bgcolor: "primary.main",
                          color: "white",
                        }}
                      >
                        <Person />
                      </Box>
                    }
                    title={
                      <Typography variant="h6" color="primary.main">
                        Personal Information
                      </Typography>
                    }
                    sx={{
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  />
                  <CardContent sx={{ p: 0 }}>
                    <InfoCard
                      icon={<Person fontSize="small" />}
                      label="Full Name"
                      value={userData.fullName}
                      color="primary"
                    />
                    <InfoCard
                      icon={<Email fontSize="small" />}
                      label="Email Address"
                      value={userData.email}
                      color="primary"
                    />
                    <InfoCard
                      icon={<Phone fontSize="small" />}
                      label="Phone Number"
                      value={userData.phone}
                      color="primary"
                    />
                    <InfoCard
                      icon={<AccountCircle fontSize="small" />}
                      label="User Type"
                      value={userData.userType}
                      color="primary"
                    />
                    <InfoCard
                      icon={<CalendarToday fontSize="small" />}
                      label="Member Since"
                      value={formatDate(userData.datetime)}
                      color="primary"
                    />
                    <InfoCard
                      icon={<Smartphone fontSize="small" />}
                      label="App Version"
                      value={userData.app_version}
                      color="primary"
                    />
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12} md={6} lg={4}>
              <Zoom in timeout={1000}>
                <Card>
                  <CardHeader
                    avatar={
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "12px",
                          bgcolor: "secondary.main",
                          color: "white",
                        }}
                      >
                        <LocationOn />
                      </Box>
                    }
                    title={
                      <Typography variant="h6" color="secondary.main">
                        Address Information
                      </Typography>
                    }
                    sx={{
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  />
                  <CardContent sx={{ p: 0 }}>
                    <InfoCard
                      icon={<Home fontSize="small" />}
                      label="City"
                      value={userData.address?.city}
                      color="secondary"
                    />
                    <InfoCard
                      icon={<LocationOn fontSize="small" />}
                      label="District"
                      value={userData.address?.district}
                      color="secondary"
                    />
                    <InfoCard
                      icon={<Map fontSize="small" />}
                      label="State/Region"
                      value={userData.address?.region}
                      color="secondary"
                    />
                    <InfoCard
                      icon={<LocationOn fontSize="small" />}
                      label="Postal Code"
                      value={userData.address?.postalCode}
                      color="secondary"
                    />
                    <InfoCard
                      icon={<Map fontSize="small" />}
                      label="Pincode"
                      value={userData.pincode}
                      color="secondary"
                    />
                    <InfoCard
                      icon={<LocationOn fontSize="small" />}
                      label="Coordinates"
                      value={
                        userData.address
                          ? `${userData.address.latitude}, ${userData.address.longitude}`
                          : null
                      }
                      color="secondary"
                    />
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>

            {/* Medical & App Information */}
            <Grid item xs={12} md={12} lg={4}>
              <Zoom in timeout={1200}>
                <Card>
                  <CardHeader
                    avatar={
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "12px",
                          bgcolor: "warning.main",
                          color: "white",
                        }}
                      >
                        <MedicalServices />
                      </Box>
                    }
                    title={
                      <Typography variant="h6" color="warning.main">
                        Medical & App Data
                      </Typography>
                    }
                    sx={{
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  />
                  <CardContent sx={{ p: 0 }}>
                    {/* <InfoCard
                      icon={<Assignment fontSize="small" />}
                      label="Patient Records"
                      value={
                        userData.patientDetails?.length > 0
                          ? `${userData.patientDetails.length} records`
                          : "No records"
                      }
                      status={userData.patientDetails?.length > 0 ? "success" : "warning"}
                      color="warning"
                    /> */}
                    <InfoCard
                      icon={<LocalHospital fontSize="small" />}
                      label="Prescriptions"
                      value={
                        userData.prescription?.length > 0
                          ? `${userData.prescription.length} prescriptions`
                          : "No prescriptions"
                      }
                      status={userData.prescription?.length > 0 ? "success" : "warning"}
                      color="warning"
                    />
                    <InfoCard
                      icon={<PhotoCamera fontSize="small" />}
                      label="Profile Picture"
                      value={userData.profilePic ? "Uploaded" : "Not available"}
                      status={userData.profilePic ? "success" : "warning"}
                      color="warning"
                    />
                    <InfoCard
                      icon={<Smartphone fontSize="small" />}
                      label="Platform"
                      value={userData.platform}
                      color="warning"
                    />
                    <InfoCard
                      icon={<VerifiedUser fontSize="small" />}
                      label="Account Status"
                      value={userData.token ? "Active" : "Inactive"}
                      status={userData.token ? "success" : "error"}
                      color="warning"
                    />
                    <InfoCard
                      icon={<Description fontSize="small" />}
                      label="Authentication Token"
                      value={userData.token ? "Available" : "Not available"}
                      status={userData.token ? "success" : "error"}
                      color="warning"
                    />
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}